import {
    Button, ClickEvent, DataSource, DataSourceMode, Event, Layout, Panel, PanelProps, SelectionMode, SlideoutDecorator
} from "@mcleod/components";
import { RecRearrangeStops } from "./RecRearrangeStops";
import { CommonDialogs } from "@mcleod/common";
import { ManageStops } from "./ManageStops";

const buttonProps = { rowBreak: false, minWidth: 128 };

export class RearrangeStopSlideout extends Panel {
    private orderId: string;
    private orderType: string;
    private rearrangeLayoutPath: string;
    private rearrangeLayout: ManageStops | RecRearrangeStops;
    private saveButton: Button = new Button({ id: "rearrangeSaveButton", caption: "Save", enabled: false, borderWidth: 0, backgroundColor: "primary", disabledTooltip: "You cannot save this record because nothing has changed.", ...buttonProps });
    private revertButton: Button = new Button({ caption: "Revert Edits", visible: false, color: "default.reverse", borderWidth: 1, borderColor: "default.reverse", ...buttonProps });

    constructor(orderType: string, orderId: string, props?: Partial<PanelProps>) {
        super({
            fillHeight: true, fillRow: true,
            backgroundColor: "defaultBackground", top: 30, padding: 0,
            borderRadius: 4, borderWidth: 1, borderShadow: true, borderColor: "strokeSecondary"
        });
        this.orderType = orderType;
        this.orderId = orderId;
        this.rearrangeLayoutPath = orderType == "R" ? "lme/dispatch/RecRearrangeStops" : "lme/dispatch/ManageStops";
    }


    public showSlideout(afterSlideInCallback: (rearrangeLayout: ManageStops | RecRearrangeStops) => void, doAfterClose: (canceled: boolean) => void) {
        if (this.orderId) {
            this.rearrangeLayout = Layout.getLayout(this.rearrangeLayoutPath) as ManageStops | RecRearrangeStops;
            if (this.rearrangeLayout instanceof ManageStops) {
                this.rearrangeLayout.buttonRevert = this.revertButton;
            }
            this.rearrangeLayout.addLayoutLoadListener(event => {
                this.showRearrangeStops(afterSlideInCallback, doAfterClose);
            })
        }
    }

    private showRearrangeStops(afterSlideInCallback, doAfterClose) {
        const addlComponents = this.rearrangeLayout instanceof ManageStops ? [this.revertButton, this.saveButton] : [this.saveButton];
        const decorator = new SlideoutDecorator({
            layout: this.rearrangeLayout,
            title: `${this.orderType == "R" ? "Reorder Stops - Recurring" : "Manage Stops -"} Order ${this.orderId}`,
            fillVerticalSpace: true,
            width: this.orderType == "R" ? "85%" : "98%",
            onClose: async () => {
                if (this.rearrangeLayout instanceof ManageStops) {
                    const manageStops = this.rearrangeLayout as ManageStops;
                    if (manageStops.addedOrEdited()) {
                        const saveChanges = await CommonDialogs.showYesNo("Save your changes before leaving?", "Save Changes");
                        if (saveChanges == undefined) {
                            return;
                        } else if (saveChanges) {
                            this.saveButton.clicked();
                        } else {
                            manageStops.close(true);
                        }
                    } else {
                        manageStops.close(true);
                    }
                } else if (this.rearrangeLayout instanceof RecRearrangeStops) {
                    this.rearrangeLayout.close(true);
                }
            },
            layoutLoadListeners: (event: Event) => {
                const recurring: boolean = this.orderType == "R"
                if (recurring) {
                    const layout = event.target as RecRearrangeStops;
                    const tableStop = layout.tableStop;
                    tableStop.dataSource.mode = DataSourceMode.UPDATE;
                    tableStop.selectionMode = SelectionMode.SINGLE;
                    tableStop.fillHeight = true;
                } else {
                    const layout = event.target as ManageStops;
                    const tableStop = layout.layoutOrderStopTable.tableStop;
                    tableStop.dataSource.mode = DataSourceMode.UPDATE;
                    tableStop.selectionMode = SelectionMode.SINGLE;
                }
            },
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                this.rearrangeLayout.doAfterClose = doAfterClose;
                this.rearrangeLayout.orderId = this.orderId;
                await this.rearrangeLayout.mainDataSource.search({ "order_id": this.orderId, "order_type": this.orderType });
                this.saveButton["doOnDataSourceChanged"] = (ds: DataSource, value: boolean) => {
                    this.saveButton.enabled = value;
                }
                this.rearrangeLayout.mainDataSource.addHasChangedComponent(this.saveButton);
                this.saveButton.addClickListener(async (event: ClickEvent) => {
                    this.saveButton.busy = true;
                    if (this.rearrangeLayout instanceof ManageStops){
                        if (this.rearrangeLayout.stopsAreValid()) {
                            this.rearrangeLayout.onSave().finally(() => {
                                this.saveButton.busy = false;
                            });
                        } else {
                            this.saveButton.busy = false;
                        }
                    } else {
                        this.rearrangeLayout.onSave().finally(() => {
                            this.saveButton.busy = false;
                        });
                    }
                });
                afterSlideInCallback(this.rearrangeLayout);
            },
            addlComponents: addlComponents,
            overlayProps: { closeOnClickOff: false, greyedBackground: true }
        });
    }

}
